import { badgeOf } from '../core/badge';
import type { Camera } from '../core/footage';
import { frameIndexAt } from '../core/telemetry';
import type { Timeline } from '../core/timeline';
import { formatClock, formatDate, formatDuration, formatTime, mapLink, placeLabel } from '../core/format';
import { readHud, type HudReading } from '../core/hud-reading';
import { CameraGrid } from './grid';
import { Hud, type HudDom } from './hud';
import type { Command } from './commands';
import { nextRate } from './commands';
import type { Library, LibraryFile, LibraryFootage } from './library';
import { nextMarker } from './markers';
import { Playback, type FrontFrame } from './playback';
import { Scrubber, type ScrubPhase } from './scrubber';
import { buildViewerModel, type ViewerModel } from './viewer-model';

const TELEMETRY_STALE_S = 0.25;
const SECOND_MS = 1000;

export interface ViewerDom {
  readonly badge: HTMLElement;
  readonly title: HTMLElement;
  readonly meta: HTMLElement;
  readonly mapLink: HTMLAnchorElement;
  readonly ai: HTMLButtonElement;
  readonly grid: HTMLElement;
  readonly hud: HudDom;
  readonly scrubber: HTMLCanvasElement;
  readonly scrubberTip: HTMLElement;
  readonly play: HTMLButtonElement;
  readonly previousMarker: HTMLButtonElement;
  readonly nextMarker: HTMLButtonElement;
  readonly clock: HTMLElement;
  readonly clockGap: HTMLElement;
  readonly rates: HTMLElement;
  readonly gridButton: HTMLButtonElement;
}

export interface ViewerOptions {
  readonly tier: number;
  readonly autostart: boolean;
  readonly onEnded: () => void;
  readonly onEngaged: () => void;
}

export class EventViewer {
  readonly model: ViewerModel;
  private readonly grid: CameraGrid;
  private readonly playback: Playback;
  private readonly hud: Hud;
  private readonly scrubber: Scrubber;
  private readonly unbind: (() => void)[] = [];
  private wasPlaying = false;
  private lastPlaying = false;

  constructor(
    private readonly dom: ViewerDom,
    private readonly library: Library,
    readonly footage: LibraryFootage,
    readonly timeline: Timeline<LibraryFile>,
    private readonly options: ViewerOptions,
  ) {
    const { tier } = options;
    this.model = buildViewerModel(library, footage, timeline, tier);
    this.renderHeading();
    this.grid = new CameraGrid(dom.grid, new Set(footage.clips.map((clip) => clip.camera)), (camera) => this.onFocus(camera));
    this.playback = new Playback(timeline, this.grid.tiles, (tile) => this.grid.visible(tile), (time) => this.onTick(time));
    this.hud = new Hud(dom.hud);
    this.scrubber = new Scrubber(dom.scrubber, dom.scrubberTip, (time, phase) => this.onScrub(time, phase));
    this.scrubber.setModel(this.model.scrubber);
    this.bindControls();
    this.playback.start(this.model.startTime);
    if (options.autostart) this.playback.clock.play();
  }

  get time(): number {
    return this.playback.clock.time();
  }

  jumpTo(time: number): void {
    this.playback.seek(time);
  }

  get focusedCamera(): Camera | undefined {
    return this.grid.focusedCamera;
  }

  command(command: Command): boolean {
    switch (command.kind) {
      case 'toggle-play':
        this.playback.toggle();
        return true;
      case 'seek-by':
        this.playback.seek(this.time + command.seconds * SECOND_MS);
        return true;
      case 'step-frame':
        this.playback.stepFrame(command.direction);
        return true;
      case 'marker':
        this.jumpToMarker(command.direction);
        return true;
      case 'focus':
        this.grid.focusIndex(command.index);
        return true;
      case 'unfocus':
        this.grid.focus(undefined);
        return true;
      case 'rate-step':
        this.setRate(nextRate(this.playback.clock.rate, command.direction));
        return true;
      case 'rate':
        this.setRate(command.rate);
        return true;
      default:
        return false;
    }
  }

  setRate(rate: number): void {
    this.playback.clock.setRate(rate);
    this.dom.rates.querySelectorAll<HTMLElement>('[data-rate]').forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset['rate']) === rate)));
  }

  pause(): void {
    this.playback.clock.pause();
  }

  destroy(): void {
    this.playback.stop();
    this.scrubber.destroy();
    this.grid.destroy();
    this.unbind.forEach((unbind) => unbind());
  }

  private renderHeading(): void {
    const info = this.library.infoOf(this.footage);
    const notes = this.library.notesOf(this.footage);
    const badge = badgeOf(info, notes, this.options.tier);
    const wall = (time: number): number => time + this.timeline.utcOffset;
    this.dom.badge.textContent = badge.label;
    this.dom.badge.dataset['tone'] = badge.tone;
    this.dom.ai.toggleAttribute('data-has-notes', notes !== undefined);
    this.dom.title.textContent = placeLabel(info);
    this.dom.meta.textContent = `${formatDate(wall(this.timeline.start))} · ${formatTime(wall(this.timeline.start))}–${formatTime(wall(this.timeline.end))} · ${formatDuration((this.timeline.end - this.timeline.start) / SECOND_MS)}`;
    const latitude = info?.latitude;
    const longitude = info?.longitude;
    this.dom.mapLink.hidden = latitude === undefined || longitude === undefined;
    if (latitude !== undefined && longitude !== undefined) this.dom.mapLink.href = mapLink(latitude, longitude);
  }

  private listen<K extends keyof HTMLElementEventMap>(target: HTMLElement, type: K, handler: (event: HTMLElementEventMap[K]) => void): void {
    target.addEventListener(type, handler);
    this.unbind.push(() => target.removeEventListener(type, handler));
  }

  private bindControls(): void {
    this.listen(this.dom.play, 'click', () => this.playback.toggle());
    this.listen(this.dom.previousMarker, 'click', () => this.jumpToMarker(-1));
    this.listen(this.dom.nextMarker, 'click', () => this.jumpToMarker(1));
    this.listen(this.dom.gridButton, 'click', () => this.grid.focus(undefined));
    this.listen(this.dom.rates, 'click', (event) => {
      const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>('[data-rate]') : null;
      if (button) this.setRate(Number(button.dataset['rate']));
    });
    this.playback.clock.setRate(Number(this.dom.rates.querySelector('[aria-pressed="true"]')?.getAttribute('data-rate') ?? 1));
  }

  private jumpToMarker(direction: 1 | -1): void {
    const marker = nextMarker(this.model.markers, this.time, direction);
    if (marker) this.playback.seek(marker.time);
  }

  private onFocus(camera: Camera | undefined): void {
    this.dom.gridButton.hidden = camera === undefined;
  }

  private onScrub(time: number, phase: ScrubPhase): void {
    if (phase === 'start') {
      this.wasPlaying = this.playback.clock.playing;
      this.playback.clock.pause();
      this.options.onEngaged();
    }
    this.playback.seek(time, phase !== 'end');
    if (phase === 'end' && this.wasPlaying) this.playback.clock.play();
  }

  private onTick(time: number): void {
    this.scrubber.setTime(time);
    const front = this.playback.frontAt(time);
    this.dom.clock.textContent = formatClock(time + this.timeline.utcOffset);
    this.dom.clockGap.hidden = front !== undefined || this.footage.clips.every((clip) => clip.camera !== 'front');
    const playing = this.playback.clock.playing;
    this.dom.play.toggleAttribute('data-playing', playing);
    this.dom.play.setAttribute('aria-label', playing ? 'Pause (Space)' : 'Play (Space)');
    this.hud.show(this.reading(this.displayedFront(front)));
    if (this.lastPlaying && !playing && time >= this.timeline.end) this.options.onEnded();
    if (!this.lastPlaying && playing) this.options.onEngaged();
    this.lastPlaying = playing;
  }

  private displayedFront(atClock: FrontFrame | undefined): FrontFrame | undefined {
    const shown = this.playback.front;
    const frontVisible = this.grid.focusedCamera === undefined || this.grid.focusedCamera === 'front';
    return frontVisible && shown && atClock && shown.placed === atClock.placed ? shown : atClock;
  }

  private reading(frame: FrontFrame | undefined): HudReading | undefined {
    const telemetry = frame && this.library.scanOf(frame.placed.clip)?.telemetry;
    if (!frame || !telemetry) return undefined;
    const index = frameIndexAt(telemetry.time, frame.mediaTime);
    if (index < 0 || frame.mediaTime - telemetry.time[index]! > TELEMETRY_STALE_S) return undefined;
    return readHud(telemetry, index);
  }
}
